import type { Maybe } from "@/domains/common/types";
import type { Uuid } from "@/domains/global/identifiers";
import { AssistantFollowUpObservable } from "@/store/assistant/AssistantFollowUpObservable";
import type {
  AssistantFollowUpIndexData,
  AssistantFollowUpModelData,
} from "@/store/assistant/types";
import type { AppStore } from "@/store/AppStore";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { makeObservable, override } from "mobx";

export class AppStoreAssistantFollowUpsStore extends BaseSyncModelStore<
  AssistantFollowUpObservable,
  AssistantFollowUpModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.AssistantFollowUp, ...injectedDeps });
    makeObservable<this, "createSyncModel">(this, {
      createSyncModel: false,
      computeIndexes: override,
      getAssistantFollowUpObservableById: false,
    });
  }

  createSyncModel(data: SyncUpdateValue<AssistantFollowUpModelData>): AssistantFollowUpObservable {
    return new AssistantFollowUpObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<AssistantFollowUpModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<AssistantFollowUpModelData>[];
  }): AssistantFollowUpIndexData {
    const syncUpdateValue =
      (optimisticUpdates.at(-1)?.value as
        | SyncUpdateValue<AssistantFollowUpModelData>
        | undefined) || remoteData;
    const modelData = syncUpdateValue?.model_data;

    return {
      assistant_id: modelData?.assistant_id ?? "",
      project_id: modelData?.project_id ?? "",
      task_id: modelData?.task_id ?? "",
      source_context_id: modelData?.source_context_id ?? "",
      status: modelData?.status ?? "",
      scheduled_for: modelData?.scheduled_for ?? "",
      created_at: modelData?.created_at ?? "",
      updated_at: modelData?.updated_at ?? "",
    };
  }

  getAssistantFollowUpObservableById({
    assistantFollowUpId,
  }: {
    assistantFollowUpId: Uuid;
  }): AssistantFollowUpObservable | undefined {
    return this.get(assistantFollowUpId);
  }
}
